import type { CSSProperties, SyntheticEvent } from "react";
import type { Conversation } from "./types";
import { t } from "../../utils/i18n";

const PALETTE = ["#0a0a0a", "#0a0a0a", "#0a0a0a", "#333333", "#666666", "#777777"];
export const colorFromId = (id: string) => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return PALETTE[Math.abs(hash) % PALETTE.length];
};
export const initials = (name: string) => (name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();

// Avant l'introduction du type dédié "watch_room" (voir Messages.tsx#startWatchTogether), une
// invitation "Regarder ensemble" était un simple message texte "🎬 Regarder ensemble : <lien>" —
// ces messages existent déjà en base (envoyés par un client pas encore mis à jour) et resteront
// affichés tels quels pour toujours si on ne les détecte pas ici. On extrait l'UUID de salle
// depuis le lien plutôt que de dépendre du texte (qui n'était même pas traduit), pour que la
// bulle ET l'aperçu de conversation redeviennent la même pastille moderne que les nouvelles
// invitations, sans toucher à la base de données.
export const extractLegacyWatchRoomId = (content: string | undefined): string | null => {
  if (!content) return null;
  const m = content.match(/\/watch\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
  return m ? m[1] : null;
};

// Le dernier message d'une conversation n'a pas forcément de texte (photo, vocal, position...) —
// il faut quand même afficher un aperçu clair sous le nom, pas "Aucun message".
export const callLogLabel = (content: string | undefined, lang: string) => {
  let call: { call_type?: string; status?: string; duration_seconds?: number } = {};
  try { call = JSON.parse(content || "{}"); } catch { call = {}; }
  const kind = call.call_type === "video" ? (t(lang,"msg_call_video_label")||"Appel vidéo") : (t(lang,"msg_call_voice_label")||"Appel vocal");
  if (call.status === "declined") return `${kind} ${t(lang,"msg_call_declined")||"refusé"}`;
  if (call.status === "missed") return `${kind} ${t(lang,"msg_call_missed")||"manqué"}`;
  const sec = call.duration_seconds || 0;
  return `${kind} · ${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
};

// Ligne séparatrice de date façon Messenger — "Aujourd'hui" / "Hier" / jour de semaine sur la
// semaine passée / date complète au-delà, plutôt qu'un fil de messages sans aucun repère de jour.
export const dateSeparatorLabel = (iso: string, lang: string) => {
  const d = new Date(iso);
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86400000);
  if (diffDays === 0) return t(lang,"msg_today")||"Aujourd'hui";
  if (diffDays === 1) return t(lang,"msg_yesterday")||"Hier";
  if (diffDays > 1 && diffDays < 7) { const l = d.toLocaleDateString(lang, { weekday: "long" }); return l.charAt(0).toUpperCase() + l.slice(1); }
  return d.toLocaleDateString(lang, { day: "2-digit", month: "2-digit", year: "numeric" });
};

export const lastMessagePreview = (conv: Conversation, lang: string) => {
  if (!conv.last_message_at) {
    // Conversation directe éphémère entièrement vidée (tous les messages ont expiré) : rien à
    // afficher — pas "Aucun message", qui laisserait penser qu'il n'y a jamais rien eu.
    return conv.disappearing ? "" : (t(lang,"msg_no_message")||"Aucun message");
  }
  switch (conv.last_message_type) {
    case "image": return t(lang,"msg_photo")||"Photo";
    case "audio": return t(lang,"msg_voice_message")||"Message vocal";
    case "location": return t(lang,"msg_location_shared")||"Position partagée";
    case "file": return conv.last_message || (t(lang,"msg_file")||"Fichier");
    case "call": return callLogLabel(conv.last_message, lang);
    case "date_confirmed": return t(lang,"msg_date_confirmed")||"Rendez-vous confirmé";
    // Le contenu réel est un JSON {room_id, video_title} (voir Messages.tsx#startWatchTogether),
    // jamais affichable tel quel — cas manquant jusqu'ici, qui laissait passer ce JSON brut en
    // aperçu (ou, pour un message plus ancien envoyé avant ce type dédié, un texte brut avec un
    // émoji 🎬, l'ancien format qu'on a justement remplacé).
    case "watch_room": return t(lang,"watch_together_cta")||"Regarder ensemble";
    default:
      if (extractLegacyWatchRoomId(conv.last_message)) return t(lang,"watch_together_cta")||"Regarder ensemble";
      return conv.last_message || (t(lang,"msg_no_message")||"Aucun message");
  }
};

export const iconBtn: CSSProperties = {
  width: 32, height: 32, borderRadius: "50%", border: "none", backgroundColor: "#F5F5F5",
  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
  flexShrink: 0, padding: 0,
};
// Empêche l'enregistrement facile d'un média (clic droit, glisser, menu contextuel iOS) —
// une vraie interdiction n'existe pas sur le web, ceci n'est qu'une dissuasion.
export const noSaveProps = {
  onContextMenu: (e: SyntheticEvent) => e.preventDefault(),
  draggable: false,
  style: { WebkitUserSelect: "none" as const, userSelect: "none" as const, WebkitTouchCallout: "none" as const },
};

export const formatBubbleTime = (iso: string) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

// Regroupement façon Messenger : deux messages consécutifs du même expéditeur, le même jour,
// à moins de 60s d'écart, forment une même "salve" — seule la dernière bulle de la salve montre
// heure+coches par défaut (le nom/avatar, lui, n'apparaît qu'en tête de salve).
export const GROUP_WINDOW_MS = 60000;
export const messagesGroup = (a: ChatMessageLike | undefined, b: ChatMessageLike | undefined) => {
  if (!a || !b) return false;
  if (a.type === "call" || b.type === "call") return false;
  if (a.user_id !== b.user_id) return false;
  if (new Date(a.created_at).toDateString() !== new Date(b.created_at).toDateString()) return false;
  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime() <= GROUP_WINDOW_MS;
};
type ChatMessageLike = { type: string; user_id: string; created_at: string };
